import type { Options } from '@popperjs/core';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';
import { usePopper } from 'react-popper';
import { useHotkeys } from 'react-hotkeys-hook';
import { useActionItemPopover } from './hooks/action-item-popover.hook';
import { useAnchorElement } from './hooks/anchor-element.hook';
import { useClickOutside } from './hooks/click-outside.hook';

const options: Options = {
  placement: 'auto',
  strategy: 'absolute',
  modifiers: [
    {
      name: 'preventOverflow',
      options: {
        altAxis: true,
        padding: 100,
      },
    },
    {
      name: 'computeStyles',
      options: {
        gpuAcceleration: false,
        adaptive: false,
      },
    },
    {
      name: 'offset',
      options: {
        offset: [0, 0],
      },
    },
  ],
};

export const ActionItemPopover = () => {
  const [actionItem, setActionItem] = React.useState<HTMLDivElement | null>(null);
  // Draggable needs the node in a ref. Without it, Draggable calls findDOMNode, which React 19 removed.
  const draggableNodeRef = useRef<HTMLDivElement | null>(null);
  const { popperContentElement, position, close } = useActionItemPopover();

  const referanceElement = useAnchorElement(position);

  useClickOutside(actionItem, close);

  const { styles, attributes, update } = usePopper(referanceElement, actionItem, options);

  const handlePopoverRef = useCallback((node: HTMLDivElement | null) => {
    draggableNodeRef.current = node;
    setActionItem(node);
  }, []);

  const PopoverElement = useMemo(
    () => (
      <Draggable nodeRef={draggableNodeRef} handle="[data-action-item-drag-handle]" bounds="body">
        <div ref={handlePopoverRef} style={styles.popper} {...attributes.popper}>
          {popperContentElement}
        </div>
      </Draggable>
    ),
    [popperContentElement, styles, attributes, handlePopoverRef],
  );

  const getPopoverElement = useCallback(() => {
    return createPortal(PopoverElement, document.body);
  }, [PopoverElement]);

  useHotkeys(
    ['esc'],
    () => {
      if (popperContentElement && referanceElement) close();
    },
    {},
    [popperContentElement, referanceElement],
  );

  useEffect(() => {
    update?.();
  }, [referanceElement]);

  return <>{popperContentElement && referanceElement && getPopoverElement()}</>;
};
